import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useShipments } from '../composables/useShipments'
//import { Shipment } from '../domain/entities/Shipment'
import { FirestoreShipmentRepository } from '../infrastructure/repositories/FirestoreShipmentRepository'
import { simpleCache } from '../utils/simpleCache'
import { useCompanyStore } from './company'
import { useAuthStore } from './auth'
import { isDateInRange } from '../utils/dateHelpers'
import type { DataSourceMode, ShipmentsTableMetadata } from '../types/shipmentsTable'
import { resolveDisplayReference } from '../utils/shipmentDisplayReference'
import { useCatalogsStore } from './catalogs'

/** Un criterio del orden de la tabla. El primero agrupa; los siguientes desempatan. */
export interface TableSortCriterion {
  field: string
  direction: 'asc' | 'desc'
}

export const useShipmentStore = defineStore('shipments', () => {
  const authStore = useAuthStore()
  const {
    shipments,
    shipmentsContainers,
    shipmentsNotifications,
    shipmentsItems,
    shipmentsCustomsDeclarations,
    isLoading,
    error,
    fetchShipments,
    loadAllContainers,
    loadAllNotifications,
    loadAllItems,
    loadAllCustomsDeclarations,
    loadContainersForCurrentPage,
    invalidateContainersCache
  } = useShipments()
  
  // Estado dual: Firestore vs BigQuery
  const dataSourceMode = ref<DataSourceMode>('firestore')
  const bigQueryShipments = ref<any[]>([])
  const bigQueryMetadata = ref<ShipmentsTableMetadata | null>(null)

  // Filtros activos
  const activeFilters = ref<Record<string, any>>({})

  // Estado de tabla (sort + paginación) — persiste durante navegación SPA
  // Orden jerárquico: el primer criterio agrupa y los siguientes ordenan dentro de
  // cada grupo. tableSortBy/tableSortDirection exponen el primer criterio para las
  // vistas guardadas y el request a BigQuery, que solo conocen uno.
  const tableSortCriteria = ref<TableSortCriterion[]>([{ field: 'idNumber', direction: 'desc' }])
  const tableSortBy = computed<string>({
    get: () => tableSortCriteria.value[0]?.field ?? 'idNumber',
    // Cambiar el campo principal desde afuera (vista asignada, preset) vuelve a un solo criterio
    set: (field) => {
      tableSortCriteria.value = [{ field, direction: tableSortCriteria.value[0]?.direction ?? 'asc' }]
    },
  })
  const tableSortDirection = computed<string>({
    get: () => tableSortCriteria.value[0]?.direction ?? 'desc',
    set: (direction) => {
      const [first, ...rest] = tableSortCriteria.value
      tableSortCriteria.value = [
        { field: first?.field ?? 'idNumber', direction: direction === 'asc' ? 'asc' : 'desc' },
        ...rest,
      ]
    },
  })
  const tableCurrentPage = ref<number>(1)

  // Watcher para debuggear cambios en filtros
  watch(activeFilters, (newVal) => {
  }, { deep: true });

  /**
   * Aplica filtros client-side a un array de shipments.
   * Se usa tanto para Firestore como para BigQuery.
   */
  const applyClientFilters = (source: any[]): any[] => {
    if (Object.keys(activeFilters.value).length === 0) {
      return source;
    }

    return source.filter((shipment: any) => {
      return Object.entries(activeFilters.value).every(([columnId, filterValue]) => {

        if (!filterValue) return true;

        // El filtro de alerts se resuelve en ShipmentTable.filteredAndSortedShipments
        // (necesita acceso al store de alertas). Lo ignoramos acá para no excluir
        // shipments por shipment.alerts === undefined.
        if (columnId === 'alerts') return true;

        // Filtro especial para notificaciones (por estado de lectura)
        if (columnId === 'notifications') {
          const shipmentNotifications = shipmentsNotifications.value[shipment.id] || []
          const hasNotifications = shipmentNotifications.length > 0
          const userId = authStore.user?.uid

          // Verificar si hay notificaciones sin leer (usuario actual está en usersView)
          const hasUnreadNotifications = userId ? shipmentNotifications.some(
            (notification: any) => notification.usersView?.includes(userId)
          ) : false

          // Verificar si hay notificaciones leídas (usuario actual NO está en usersView)
          const hasReadNotifications = userId ? shipmentNotifications.some(
            (notification: any) => !notification.usersView?.includes(userId)
          ) : false

          if (Array.isArray(filterValue)) {
            // Si el array está vacío, mostrar todos
            if (filterValue.length === 0) return true

            // Verificar cada opción seleccionada
            const result = filterValue.some((value: string) => {
              if (value === 'empty') return !hasNotifications
              if (value === 'unread') return hasUnreadNotifications
              if (value === 'read') return hasReadNotifications
              return false
            })

            return result
          }

          // Para filtro simple (string)
          if (filterValue === 'empty') return !hasNotifications
          if (filterValue === 'unread') return hasUnreadNotifications
          if (filterValue === 'read') return hasReadNotifications

          return true
        }

        if(columnId === 'initialFreight' || columnId === 'finalFreight') {
          const freight = shipment[columnId]

          if (filterValue === 'empty') return !freight

          if (!freight) return false

          // Si el filtro es un objeto con currency y range (filtro avanzado)
          if (typeof filterValue === 'object' && filterValue.currency) {
            // Verificar coincidencia de moneda
            if (freight.currency !== filterValue.currency) return false

            // Si hay rango, aplicarlo
            if (filterValue.range) {
              const value = Number(freight.value || 0)
              if (filterValue.range === '0-1000') {
                return value >= 0 && value <= 1000
              } else if (filterValue.range === '1001-10000') {
                return value > 1000 && value <= 10000
              } else if (filterValue.range === '10000+') {
                return value > 10000
              }
            }
            return true
          }

          // Filtro simple: comparar valor numérico
          return Number(freight.value) === Number(filterValue)
        }

        // Para campos dentro de milestones
        // Algunos campos como 'eta', 'etd', 'ata', 'atd' están en milestones pero no tienen el prefijo
        const milestoneFields = ['eta', 'etd', 'ata', 'atd', 'requestedPickupDate', 'confirmedPickupDate', 'requestedDeliveryDate', 'confirmedDeliveryDate']
        const isMilestoneField = columnId.includes('milestones.') || milestoneFields.includes(columnId)

        if (isMilestoneField) {
          const field = columnId.includes('milestones.') ? columnId.split('.')[1] : columnId
          const milestoneValue = shipment.milestones?.[field] || shipment[field]

          if (filterValue === 'empty') return !milestoneValue

          if (!milestoneValue) return false

          if (typeof filterValue === 'object' && 'from' in filterValue) {
            return isDateInRange(milestoneValue, filterValue.from, filterValue.to)
          }

          return String(milestoneValue).toLowerCase()
            .includes(String(filterValue).toLowerCase())
        }

        // Para campos de container (formato legacy: containerNumber/containerType, formato dinámico: field__container)
        const isContainerField = columnId.startsWith('container') || columnId.endsWith('__container')
        if (isContainerField) {
          // En BigQuery, containers ya están embebidos en shipment.containers
          // En Firestore, se buscan en shipmentsContainers map
          const containers = shipmentsContainers.value[shipment.id] || shipment.containers || []

          if (filterValue === 'empty') return !containers.length

          if (!containers.length) return false

          // Determinar el nombre real del campo dentro del container
          let containerFieldName: string
          if (columnId.endsWith('__container')) {
            containerFieldName = columnId.replace('__container', '')
          } else if (columnId === 'containerNumber') {
            containerFieldName = 'number'
          } else if (columnId === 'containerType') {
            containerFieldName = 'type'
          } else {
            containerFieldName = columnId.replace('container', '').toLowerCase()
          }

          return containers.some((container: any) => {
            const containerValue = container[containerFieldName]
            if (!containerValue) return false

            if (Array.isArray(filterValue)) {
              return filterValue.length === 0 || filterValue.includes(containerValue)
            }
            return String(containerValue).toLowerCase()
              .includes(String(filterValue).toLowerCase())
          })
        }

        // Filtro combinado idNumber + tracking
        if (columnId === 'idNumber' && typeof filterValue === 'object' && 'tracking' in filterValue) {
          const { search, tracking } = filterValue as { search: string | null; tracking: string | null }
          if (search) {
            const idVal = String(shipment.idNumber || '').toLowerCase()
            if (!idVal.includes(search.toLowerCase())) return false
          }
          if (tracking === 'active' && !shipment.isTracking) return false
          if (tracking === 'inactive' && shipment.isTracking) return false
          return true
        }

        // Para el resto de campos
        const shipmentValue = shipment[columnId]

        if (filterValue === 'empty') return !shipmentValue || (Array.isArray(shipmentValue) && shipmentValue.length === 0)

        if (Array.isArray(filterValue)) {
          if (filterValue.length === 0) return true
          const includesEmpty = filterValue.includes('empty')
          const otherValues = includesEmpty ? filterValue.filter((v: string) => v !== 'empty') : filterValue
          const matchesEmpty = includesEmpty && (!shipmentValue || (Array.isArray(shipmentValue) && shipmentValue.length === 0))
          // Los ids de las opciones del filtro siempre son strings; el valor guardado
          // puede ser boolean o number (campos custom), así que se comparan como texto.
          // Además, un booleano sin valor se muestra como "No" en la tabla: para que el
          // filtro coincida con lo que se ve, se normaliza a 'false'.
          const isBooleanFilter = otherValues.every((v: any) => v === 'true' || v === 'false')
          const normalizedValue =
            isBooleanFilter && (shipmentValue === undefined || shipmentValue === null || shipmentValue === '')
              ? 'false'
              : String(shipmentValue)
          const matchesOther = otherValues.length > 0 && (
            Array.isArray(shipmentValue)
              ? shipmentValue.some((v: any) => otherValues.some((o: any) => String(o) === String(v)))
              : otherValues.some((v: any) => String(v) === normalizedValue)
          )
          return matchesEmpty || matchesOther
        } else if (typeof filterValue === 'object' && 'from' in filterValue) {
          // Para filtros de fecha - usar helper para manejar Timestamps de Firebase
          if (!shipmentValue) return false

          const resultado = isDateInRange(shipmentValue, filterValue.from, filterValue.to)

          // Log para debugging
          if (import.meta.env.DEV) {
          }

          return resultado
        } else {
          // Para filtros de texto
          // ✅ Si el valor del shipment es un array (campos de referencia), buscar dentro del array
          if (Array.isArray(shipmentValue)) {
            // Buscar si algún elemento del array contiene el texto de búsqueda
            return shipmentValue.some((item: any) =>
              String(item || '').toLowerCase().includes(String(filterValue).toLowerCase())
            )
          }

          // Para valores normales (string, number, etc.)
          return String(shipmentValue || '')
            .toLowerCase()
            .includes(String(filterValue).toLowerCase())
        }
      })
    })
  }

  // Computed para shipments filtrados
  const filteredShipments = computed(() => {
    const catalogsStore = useCatalogsStore()
    const globalConfig = catalogsStore.configs?.GLOBAL_CONFIG
    const displayConfig = {
      shipmentDisplayField: globalConfig?.shipmentDisplayField ?? null,
      shipmentDisplayMode: (globalConfig?.shipmentDisplayMode as 'replace' | 'complement') ?? 'complement',
    }

    if (dataSourceMode.value === 'bigquery') {
      // En modo BigQuery, los datos ya vienen del API con filtros principales aplicados.
      // Aplicamos filtros client-side adicionales (notificaciones, etc.) y añadimos notifications de Firestore.
      const filtered = applyClientFilters(bigQueryShipments.value)
      return filtered.map((shipment: any) => ({
        ...shipment,
        // Containers/items ya están embebidos desde la normalización de BigQuery
        notifications: shipmentsNotifications.value[shipment.id] || [],
        displayReference: resolveDisplayReference(shipment, displayConfig),
      }))
    }

    // Modo Firestore: filtrar y mapear containers/items/notifications/customs desde sus maps
    const filtered = applyClientFilters(shipments.value)
    return filtered.map((shipment: any) => ({
      ...shipment,
      containers: shipmentsContainers.value[shipment.id] || [],
      notifications: shipmentsNotifications.value[shipment.id] || [],
      items: shipmentsItems.value[shipment.id] || [],
      customsDeclarations: shipmentsCustomsDeclarations.value[shipment.id] || [],
      displayReference: resolveDisplayReference(shipment, displayConfig),
    }))
  })

  const mainListShipments = computed(() =>
    filteredShipments.value.filter((s: any) => (s.consolidationType || 'standalone') !== 'house')
  )

  // Método para actualizar filtros
  const updateFilters = (newFilters: Record<string, any>) => {
    activeFilters.value = newFilters
  }

  // Método para recargar shipments (solo cuando sea necesario)
  const refreshShipments = async () => {
    try {
      await fetchShipments()
    } catch (e) {
      console.error('Error refreshing shipments:', e)
    }
  }
  
  // ✅ Método para forzar refresh invalidando el caché
  const forceRefresh = async (filters = {}) => {
    try {
      const companyStore = useCompanyStore()

      // Limpiar datos BigQuery si existen
      bigQueryShipments.value = []
      bigQueryMetadata.value = null

      simpleCache.clear(`shipments_${companyStore.selectedCompanyId}`)
      simpleCache.clear(`containers_${companyStore.selectedCompanyId}`)
      simpleCache.clear(`notifications_${companyStore.selectedCompanyId}`)
      simpleCache.clear(`customs_${companyStore.selectedCompanyId}`)
      
      
      // Usar el parámetro force=true para invalidar caché automáticamente
      await fetchShipments(filters, true)
      
      // Recargar containers si es necesario
      if (shipmentsContainers.value && Object.keys(shipmentsContainers.value).length > 0) {
        await loadContainersForCurrentPage()
      }
      
      // Recargar notificaciones para filtros
      await loadAllNotifications()
    } catch (e) {
      console.error('Error forcing refresh:', e)
    }
  }
  
  // ✅ Método para invalidar caché después de un cambio
  const invalidateCache = () => {
    const companyStore = useCompanyStore()
    simpleCache.clear(`shipments_${companyStore.selectedCompanyId}`)
    simpleCache.clear(`containers_${companyStore.selectedCompanyId}`)
    simpleCache.clear(`notifications_${companyStore.selectedCompanyId}`)
    simpleCache.clear(`customs_${companyStore.selectedCompanyId}`)
  }
  
  // Métodos para modo dual BigQuery
  const setDataSourceMode = (mode: DataSourceMode) => {
    dataSourceMode.value = mode
  }

  const setBigQueryData = (data: any[], metadata: ShipmentsTableMetadata) => {
    bigQueryShipments.value = data
    bigQueryMetadata.value = metadata

    // Poblar shipmentsContainers y shipmentsItems desde los datos normalizados
    // ShipmentTableRow lee containers de shipmentStore.shipmentsContainers[shipment.id]
    const containersMap: Record<string, any[]> = {}
    const itemsMap: Record<string, any[]> = {}
    const customsMap: Record<string, any[]> = {}
    for (const shipment of data) {
      if (shipment.id) {
        if (shipment.containers && shipment.containers.length > 0) {
          containersMap[shipment.id] = shipment.containers
        }
        if (shipment.items && shipment.items.length > 0) {
          itemsMap[shipment.id] = shipment.items
        }
        if (shipment.customsDeclarations && shipment.customsDeclarations.length > 0) {
          customsMap[shipment.id] = shipment.customsDeclarations
        }
      }
    }
    shipmentsContainers.value = containersMap
    shipmentsItems.value = itemsMap
    shipmentsCustomsDeclarations.value = customsMap
  }

  // Nuevo método para actualizar un solo embarque
  const updateSingleShipment = async (shipmentId: string, companyId: string) => {
    const repository = new FirestoreShipmentRepository()
    try {
      const updatedShipment = await repository.getShipment(shipmentId, companyId)
      
      // Reasignar array completo para disparar reactividad con shallowRef
      const index = shipments.value.findIndex(s => s.id === shipmentId)
      if (index !== -1) {
        const catalogsStore = useCatalogsStore()
        const globalConfig = catalogsStore.configs?.GLOBAL_CONFIG
        const displayConfig = {
          shipmentDisplayField: globalConfig?.shipmentDisplayField ?? null,
          shipmentDisplayMode: (globalConfig?.shipmentDisplayMode as 'replace' | 'complement') ?? 'complement',
        }
        const updated = [...shipments.value]
        updated[index] = {
          ...updatedShipment,
          displayReference: resolveDisplayReference(updatedShipment, displayConfig),
        }
        shipments.value = updated
      }
    } catch (error) {
      console.error('Error updating single shipment:', error)
    }
  }

  return {
    shipments,
    shipmentsContainers,
    shipmentsNotifications,
    shipmentsItems,
    shipmentsCustomsDeclarations,
    filteredShipments,
    mainListShipments,
    isLoading,
    error,
    activeFilters,
    tableSortCriteria,
    tableSortBy,
    tableSortDirection,
    tableCurrentPage,
    updateFilters,
    refreshShipments,
    forceRefresh,
    invalidateCache,
    updateSingleShipment,
    loadAllContainers,
    loadAllNotifications,
    loadAllItems,
    loadAllCustomsDeclarations,
    loadContainersForCurrentPage,
    invalidateContainersCache,
    // BigQuery dual mode
    dataSourceMode,
    bigQueryShipments,
    bigQueryMetadata,
    setDataSourceMode,
    setBigQueryData,
  }
})
